import { ProductCardProps } from '@design-system/product-card';
import { setLocalStorageData } from '@phx-husky/local-storage';
import { kebabCase } from 'lodash';

import TMPLinkWrap from '~/components/TMPLinkWrap';
import { headingBlockMapper } from '~/mappers/headingBlock';
import { imageMapper } from '~/mappers/image';
import {
  ItemListingProductCardCategory,
  ItemListingProductCardRef,
} from '~/mappers/itemListing/models';
import { traitGroupMapper } from '~/mappers/traitGroup';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';

import {
  getDescriptionProps,
  ProductCardCategory,
  ProductCardDataCommon,
  ProductCardRef,
} from './models';

const NO_DESCRIPTION = 'none';
const CMS_DESCRIPTION = 'page data';

const getDescription = ({
  descriptionField,
  cmsDescription,
  productData,
}: getDescriptionProps): string | null => {
  switch (descriptionField) {
    case NO_DESCRIPTION:
      return null;
    case CMS_DESCRIPTION:
      return cmsDescription;
    default:
      return productData[descriptionField];
  }
};

const productCardMapper = (
  {
    className,
    productData,
    listingData: { label, description: cmsDescription },
    fullPath,
    onClickProductCard,
    categories,
    listingImage,
    useFallbackImage,
  }: ProductCardDataCommon,
  {
    imageSettings,
    cardContentUI,
    variant,
    showLabels,
    descriptionField,
    showCategory,
  }:
    | Omit<ItemListingProductCardCategory, 'type' | 'typeName'>
    | Omit<ItemListingProductCardRef, 'type' | 'typeName'>
): ProductCardProps => {
  const { heroImage, productTitle, productFeatures } = productData;

  const cardImage =
    useFallbackImage && listingImage[0]
      ? imageMapper({ ...listingImage[0], ...imageSettings })
      : heroImage &&
        imageMapper({
          ...{
            alt: heroImage.alt,
            image: {
              gatsbyImageData:
                heroImage.localAsset.childImageSharp?.gatsbyImageData,
              url: heroImage.url,
            },
          },
          ...imageSettings,
        });

  return {
    ...(className && {
      className,
    }),
    ...(cardImage && {
      cardMedia: {
        image: cardImage,
        mediaVariant: imageSettings.rounded ? 'rounded' : 'standard',
      },
    }),
    link: {
      component: TMPLinkWrap,
      props: {
        href: fullPath,
        onClick: onClickProductCard ? () => onClickProductCard() : undefined,
      },
    },
    cardContent: {
      headingBlock: headingBlockMapper({
        ui: cardContentUI,
        headline: productTitle,
        descriptionRte: getDescription({
          descriptionField,
          cmsDescription,
          productData,
        }),
        detail: null,
        link: [],
        button: [],
      }),
      ...(showCategory &&
        categories?.[0]?.title && {
          category: categories?.[0]?.title,
        }),
    },
    traitGroup: traitGroupMapper({
      direction: 'horizontal',
      size: 'small',
      textAlign: 'start',
      withBackground: false,
      items: productFeatures.map((item) => ({
        description: item.description,
        image: { image: item.image, alt: item.alt },
      })),
    }),
    ...(showLabels &&
      label[0]?.content && {
        cardLabel: {
          labelContent: label[0].content,
          srOnlyLabelPrefix: label[0].srPrefix,
          labelVariant: label[0].variant,
          labelSize: label[0].size,
        },
      }),
    variant,
    categories: categories.map(({ title }) => kebabCase(title)),
  } as ProductCardProps;
};

export const productCardCategoryMapper = (
  {
    productData,
    listingData,
    fullPath,
    className,
    categories,
    listingImage,
    useFallbackImage,
  }: ProductCardCategory,
  settings: Omit<ItemListingProductCardCategory, 'type' | 'typeName'>,
  index
): ProductCardProps => {
  const onCLickProductCard = () => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex, index + 1);
    gtmService.emitSelectItem(productData, { index: index + 1 });
  };

  return productCardMapper(
    {
      productData,
      listingData: listingData || {},
      fullPath,
      className,
      onClickProductCard: onCLickProductCard,
      categories,
      listingImage,
      useFallbackImage,
    },
    settings
  );
};

export const productCardRefMapper = (
  {
    className,
    listingData,
    fields: { fullPath, productData },
    categories,
    listingImage,
    useFallbackImage,
  }: ProductCardRef,
  settings: Omit<ItemListingProductCardRef, 'type' | 'typeName'>,
  index
  // eslint-disable-next-line sonarjs/no-identical-functions
): ProductCardProps => {
  const onCLickProductCard = () => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex, index + 1);
    gtmService.emitSelectItem(productData, { index: index + 1 });
  };

  return productCardMapper(
    {
      className,
      productData,
      listingData: listingData || {},
      fullPath,
      onClickProductCard: onCLickProductCard,
      categories,
      listingImage,
      useFallbackImage,
    },
    settings
  );
};
